import { Component, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco'; // cspell:disable-line
import { CheckboxModule } from 'primeng/checkbox'; // cspell:disable-line
import { TooltipModule } from 'primeng/tooltip'; // cspell:disable-line
import { LucideAngularModule } from 'lucide-angular';
import { FormatCO2ePipe } from 'src/app/pipes/format-co2e.pipe';
import { ReductionTip, TipType } from '../../../models/reduction-tip';
import {
  isSelectableTip,
  TIP_I18N_KEY,
  TIP_ICON,
} from '../../../utils/reduction-tip-savings';
import { ReductionTipItemComponent } from '../reduction-tip-item/reduction-tip-item.component';
import { ReductionTipsService } from 'src/app/impact/services/reduction-tips.service';

/** Tips of one type, which the API emits once per trip element. */
export interface TipGroup {
  type: TipType;
  tips: ReductionTip[];
}

@Component({
  selector: 'app-reduction-tip-group',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    CheckboxModule,
    TooltipModule,
    LucideAngularModule,
    FormatCO2ePipe,
    ReductionTipItemComponent,
  ],
  templateUrl: './reduction-tip-group.component.html',
})
export class ReductionTipGroupComponent {
  group = input.required<TipGroup>();

  protected tipsService = inject(ReductionTipsService);

  expanded = signal(false);

  tips = computed(() => this.group().tips);
  icon = computed(() => TIP_ICON[this.group().type]);
  i18nKey = computed(() => `reductionTips.${TIP_I18N_KEY[this.group().type]}`);
  selectable = computed(() => isSelectableTip(this.tips()[0]));
  isCommitted = computed(() => this.tips().some((tip) => tip.isCommitted));

  selectedCount = computed(
    () =>
      this.tips().filter((tip) => this.tipsService.isSelected(tip.id)).length,
  );
  allSelected = computed(() => this.selectedCount() === this.tips().length);
  partiallySelected = computed(
    () => this.selectedCount() > 0 && !this.allSelected(),
  );

  /**
   * Savings of the selected tips once the user started picking, otherwise the
   * full potential of the group.
   */
  savings = computed(() => {
    const relevant =
      this.selectedCount() > 0
        ? this.tips().filter((tip) => this.tipsService.isSelected(tip.id))
        : this.tips();

    return relevant.reduce(
      (total, tip) => total + this.tipsService.savingOf(tip),
      0,
    );
  });

  /** `savings()` is a trip total; the header follows the current view mode. */
  displaySavings = computed(() =>
    this.tipsService.perPerson()
      ? this.savings() / Math.max(1, this.tipsService.travellers())
      : this.savings(),
  );

  toggleExpanded(): void {
    this.expanded.update((value) => !value);
  }

  /** The checkbox is bound one-way, so the click means "flip the group". */
  onToggleAll(): void {
    this.tipsService.setSelection(
      this.tips().map((tip) => tip.id),
      !this.allSelected(),
    );
  }
}
